import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import logoUrl from '../assets/logo.webp'
import { paletteCss } from './palette.ts'

export type Brand = {
  name: string
  logo: string
  primary: string
  accent: string
  roles: Record<string, string>
  platforms: Record<string, string>
}


// Design-system defaults: eucalyptus primary + apricot accent (docs/superpowers/specs/2026-07-27-design-system.md).
// Exported so the settings page can offer an honest "reset to defaults".
export const DEFAULT_BRAND: Brand = {
  name: 'Astute Early Years',
  logo: logoUrl,
  primary: '#3f6f5b',
  accent: '#dc9a5c',
  // Empty means "use Astute's original wording", resolved server-side in
  // server/merge-defaults.ts. Storing the defaults here would fork them.
  roles: {},
  platforms: {},
}
const KEY = 'aeys-vco-brand'
// The derived stylesheet is cached under its own key and re-applied by the boot
// script in index.html, before first paint. Without it every load would flash
// the shipped cream palette before React mounted and repainted it.
const PALETTE_KEY = 'aeys-vco-palette'
const PALETTE_ID = 'brand-palette'

// A persisted value that should be a string but might not be — same family
// as asRecord() below (roles/platforms) and SettingsPage's hasOverride: a
// corrupt or hand-edited localStorage entry that bypasses the app's own
// setters. Every direct consumer of brand.name calls a string method on it
// (`.trim()` here, `escapeHtml()` in src/lib/export.ts), so an unguarded
// non-string value crashes whichever page renders a document first.
function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback
}

/**
 * Brand kit -> merge fields for a render request. Blank overrides are omitted
 * rather than sent empty, so the server falls back to Astute's wording.
 */
export function brandToMergeFields(brand: Brand): Record<string, string> {
  const out: Record<string, string> = {}
  const name = asString(brand.name, '')
  if (name.trim()) out.service_name = name.trim()
  for (const source of [brand.roles ?? {}, brand.platforms ?? {}]) {
    for (const [k, v] of Object.entries(source)) {
      if (typeof v === 'string' && v.trim()) out[k] = v.trim()
    }
  }
  return out
}

// A persisted `roles`/`platforms` value that isn't a plain object — null, a
// number, a string, an array — from hand-edited or otherwise corrupt
// localStorage. Object spread alone doesn't catch this: `{...null}` and
// `{...42}` both quietly yield `{}`, but `{...'x'}` and `{...[1, 2]}` leak
// indexed keys ('0', '1', ...) instead. An explicit shape check is needed.
function asRecord(value: unknown): Record<string, string> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, string>)
    : {}
}

const Ctx = createContext<{ brand: Brand; setBrand: (b: Brand) => void }>({
  brand: DEFAULT_BRAND,
  setBrand: () => {},
})

export function BrandKitProvider({ children }: { children: ReactNode }) {
  const [brand, setBrandState] = useState<Brand>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(KEY) ?? '{}')
      const merged = { ...DEFAULT_BRAND, ...stored }
      // Always source the logo from the current bundle: its URL carries a build
      // hash, so a persisted value would 404 after the next build. Not
      // user-editable in Phase 0 anyway.
      merged.logo = DEFAULT_BRAND.logo
      // A brand kit persisted before roles/platforms existed is already handled
      // by the top-level spread above (a source object missing a key never
      // overwrites the default). What still needs guarding is a key that IS
      // present but corrupt — see asRecord() above — so it can't propagate a
      // non-object into app state and crash downstream Object.values/entries
      // calls (e.g. SettingsPage's hasOverride).
      merged.roles = { ...DEFAULT_BRAND.roles, ...asRecord(stored.roles) }
      merged.platforms = { ...DEFAULT_BRAND.platforms, ...asRecord(stored.platforms) }
      // Same corrupt-localStorage family as roles/platforms above, for the
      // one plain-string field: brand.name.trim() (brandToMergeFields) and
      // escapeHtml(brand.name) (src/lib/export.ts) both assume a string.
      merged.name = asString(stored.name, DEFAULT_BRAND.name)
      return merged
    } catch {
      return DEFAULT_BRAND
    }
  })

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(brand))
  }, [brand])

  // Reskin the design system: a *customised* brand.primary repaints the accent
  // and re-hues the surfaces around it (see ./palette.ts). At the default we
  // add nothing, so index.css's hand-tuned cream-and-eucalyptus pairing governs.
  //
  // A stylesheet rather than inline custom properties on <html>: the derived
  // tokens differ between light and dark, and one stylesheet carries the
  // explicit-light, explicit-dark and follow-the-OS cases together, where an
  // inline style would pin a single theme's values across both.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const css = paletteCss(brand.primary, DEFAULT_BRAND.primary)
    // null = an unreadable colour, mid-typing. Leave the last good palette up.
    if (css === null) return
    try {
      if (css) localStorage.setItem(PALETTE_KEY, css)
      else localStorage.removeItem(PALETTE_KEY)
    } catch {
      /* ignore persistence failures — the live stylesheet below still applies */
    }
    const existing = document.getElementById(PALETTE_ID)
    if (!css) {
      existing?.remove()
      return
    }
    const el = existing ?? document.head.appendChild(document.createElement('style'))
    el.id = PALETTE_ID
    el.textContent = css
  }, [brand.primary])

  return <Ctx.Provider value={{ brand, setBrand: setBrandState }}>{children}</Ctx.Provider>
}

export const useBrandKit = () => useContext(Ctx)
